import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { useLazyGetHstImportFileUrlQuery } from "store/queries/integration";
import {
  HstImportFileKind,
  IHstImportBatchSummary,
} from "types/HstImportBatch";

import { apiErrorMessage } from "../../utils/hstImports";

interface IHstImportFileLinksProps {
  batch: IHstImportBatchSummary;
  showFileNames?: boolean;
}

const FILES: Array<{
  which: HstImportFileKind;
  shortLabel: string;
  label: string;
  retained: (b: IHstImportBatchSummary) => boolean;
  fileName: (b: IHstImportBatchSummary) => string | null;
}> = [
  {
    which: "scheduledAppointments",
    shortLabel: "Appts",
    label: "Scheduled appointments",
    retained: (b) => b.hasScheduledAppointmentsFile,
    fileName: (b) => b.scheduledAppointmentsFileName,
  },
  {
    which: "insuranceInfo",
    shortLabel: "Insurance",
    label: "Insurance info",
    retained: (b) => b.hasInsuranceInfoFile,
    fileName: (b) => b.insuranceInfoFileName,
  },
];

export function HstImportFileLinks({
  batch,
  showFileNames = false,
}: IHstImportFileLinksProps) {
  const toastRef = useContext(ToastContext);
  const [getFileUrl] = useLazyGetHstImportFileUrlQuery();
  const [loadingWhich, setLoadingWhich] = useState<HstImportFileKind | null>(
    null
  );

  const openFile = async (which: HstImportFileKind) => {
    // Open the tab inside the click so the browser does not treat it as a popup;
    // the signed URL is filled in once the API answers.
    const newTab = window.open("", "_blank");
    setLoadingWhich(which);
    try {
      const result = await getFileUrl({ id: batch.id, which }).unwrap();
      if (newTab) {
        newTab.opener = null;
        newTab.location.href = result.data.url;
      } else {
        window.location.assign(result.data.url);
      }
    } catch (error) {
      newTab?.close();
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't open the file",
        detail: apiErrorMessage(error).message,
      });
    } finally {
      setLoadingWhich(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-1">
      {FILES.map((file) => {
        const retained = file.retained(batch);
        const name = file.fileName(batch);
        let label = showFileNames ? name || file.label : file.shortLabel;
        if (showFileNames && !retained) label = `${label} (not retained)`;
        return (
          <Button
            key={file.which}
            label={label}
            icon="pi pi-download"
            className="p-button-text p-button-sm"
            disabled={!retained}
            loading={loadingWhich === file.which}
            onClick={() => openFile(file.which)}
            tooltip={
              retained
                ? `${file.label}${name ? `: ${name}` : ""}`
                : `${file.label} file was not retained`
            }
            tooltipOptions={{ showOnDisabled: true, position: "top" }}
            aria-label={`Download ${file.label.toLowerCase()} file`}
          />
        );
      })}
    </div>
  );
}
